import { useLot, useReferentiel } from 'api'
import { AxiosError } from 'axios'
import { superFetch } from 'commons'
import { Button } from 'components/atoms'
import ChartComponent from 'components/ChartComponent'
import { SelectField, DateField } from 'components/form'
import { Formik, Form } from 'formik'
import { downloadCSV } from 'helpers/downloadFile'
import { useTheme } from 'hooks'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaDownload, FaInfoCircle, FaUpload } from 'react-icons/fa'
import { useMutation } from 'react-query'
import { useSelector } from 'react-redux'
import { CustomInput, Label } from 'reactstrap'
import { getLotId } from 'store/selection'
import * as Yup from 'yup'
import { ProgramDeleteButton } from './ProgramDeleteButton'

export const ProgramCreate: React.FC = () => {
  const { ListCHRPas, ListDesUnites } = useReferentiel()
  const [file, setFile] = useState<File | null>(null)
  const lotId = useSelector(getLotId)
  const [programSeries, setProgramSeries] = useState([])
  const [hasProgram, setHasProgram] = useState(false)
  const lot = useLot(lotId as number)
  const lotDateDebut = lot?.Debut
  const lotDateFin = lot?.Fin

  const initialValues = {
    Debut: lotDateDebut,
    Fin: lotDateFin,
    Pas: 9,
    Unite: 11,
  }

  const { t } = useTranslation()
  const theme = useTheme() as any

  const [mutateDownloadTemplate, downloadTemplateResult] = useMutation(
    (values: any) => {
      return superFetch({
        url: `program/${lotId}/template`,
        method: 'POST',
        body: {
          dateDebut: values.Debut,
          dateFin: values.Fin,
          pas: values.Pas,
          unite: values.Unite,
        },
        responseType: 'blob',
      })
    },
    {
      onSuccess: (data, variables) => {
        downloadCSV(
          `programme_${lot?.Nom}_${formatDateOnly(variables.Debut)}_${formatDateOnly(variables.Fin)}.csv`,
          data
        )
      },
    },
  )

  const formatDateOnly = (date: string) => {
    return date.split('T')[0]
  }

  const [mutateUpload, uploadResult] = useMutation(
    ({ file, values }: { file: File; values: any }) => {
      const formData = new FormData()
      formData.append('file', file)

      const params = new URLSearchParams()
      const debut = formatDateOnly(values.Debut)
      const fin = formatDateOnly(values.Fin)

      params.set('dateDebut', debut)
      params.set('dateFin', fin)
      params.set('pas', values.Pas)
      params.set('unite', values.Unite)

      return superFetch({
        url: `program/${lotId}/upload?${params.toString()}`,
        method: 'POST',
        body: formData,
      })
    },
    {
      useErrorBoundary: false,
      throwOnError: false,
      onSuccess: async () => {
        await loadCurve()
      },
    },
  )

  const validationSchema = Yup.object().shape(
    {
      Debut: Yup.date()
        .when('Fin', (Fin: any, schema: any) => Fin && schema.max(Fin, t('global.startDateError')))
        .min(lotDateDebut as string, t('lot.program.startAfterLotStart'))
        .required(t('global.requiredField')),
      Fin: Yup.date()
        .when(
          'Debut',
          (Debut: any, schema: any) => Debut && schema.min(Debut, t('global.endDateError')),
        )
        .max(lotDateFin as string, t('lot.program.endBeforeLotEnd'))
        .required(t('global.requiredField')),
      Unite: Yup.number().required(t('global.requiredField')),
      Pas: Yup.number().required(t('global.requiredField')),
    },
    [['Debut', 'Fin']],
  )

  const loadCurve = async () => {
    const result = await superFetch({
      url: `program/${lotId}/curve`,
      method: 'GET',
    })
    const series = result.series ?? []
    setProgramSeries(result.series)
    setHasProgram(series.length > 0)
  }

  useEffect(() => {
    loadCurve()
  }, [lotId])

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => mutateDownloadTemplate(values)}
      validationSchema={validationSchema}
    >
      {({ isValid, values }) => (
        <Form>
          <section
            css={{
              display: 'grid',
              gridGap: theme.spacing,
              gridTemplateColumns: 'repeat(2, 1fr)',
              paddingTop: theme.spacing,
              paddingBottom: theme.spacing,
            }}
          >
            <DateField name="Debut" label={t('contract.consommationPopover.debut')} />
            <DateField name="Fin" label={t('contract.consommationPopover.fin')} />
            <SelectField
              name="Unite"
              label={t('contract.consommationPopover.unite')}
              options={ListDesUnites}
            />
            <SelectField
              name="Pas"
              label={t('contract.consommationPopover.pas')}
              options={ListCHRPas}
            />
          </section>
          <section css={{ paddingBottom: theme.spacing * 1.5 }}>
            <Button
              type="submit"
              Icon={<FaDownload />}
              disabled={!isValid || downloadTemplateResult.status == 'loading'}
            >
              {t('contract.consommationPopover.downloadTemplate')}
            </Button>
          </section>
          <section css={{ paddingBottom: theme.spacing }}>
            <Label for="upload-program-doc">{t('lot.program.programFileImport')}</Label>
          </section>
          <p> <FaInfoCircle /> {t('lot.program.helperText')} </p>
          <section
            css={{ display: 'grid', gridTemplateColumns: 'repeat(2, 2fr)', gridGap: theme.spacing }}
          >
            <CustomInput
              type="file"
              id="upload-program-doc"
              onChange={(e: any) => {
                const selectedFile = e.target.files?.[0]
                setFile(selectedFile)
                e.target.value = null
              }}
            ></CustomInput>
            <Button
              Icon={<FaUpload />}
              disabled={!isValid || !file || uploadResult.status == 'loading'}
              onClick={() =>
                mutateUpload({
                  file: file!,
                  values: values,
                })
              }
            >
              {t('lot.program.importProgramFile')}
            </Button>
            {uploadResult.status == 'success' && (
              <div style={{ color: 'green', marginTop: 10 }}>{String(uploadResult.data)}</div>
            )}

            {uploadResult.status == 'error' && (
              <div style={{ color: 'red', marginTop: 10 }}>
                {(uploadResult.error as AxiosError<any>)?.response?.data?.Message}
              </div>
            )}
          </section>

          {hasProgram && (
            <section css={{ marginTop: theme.spacing }}>
              <ProgramDeleteButton onDeleted={loadCurve}/>
              <ChartComponent series={programSeries} title={t('lot.program.volumeCurve')} />
            </section>
          )}
        </Form>
      )}
    </Formik>
  )
}
